import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  AlertTriangle,
  Loader2,
  ChevronRight,
  Filter,
  Dumbbell,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

type Team = {
  id: string;
  team_name: string;
};

type Training = {
  id: string;
  date: string;
  team_id: string | null;
  notes: string | null;
};

type ViewFilter = "all" | "upcoming" | "past";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

function isUpcoming(iso: string) {
  return new Date(iso) >= new Date();
}

function groupByMonth(trainings: Training[]): { label: string; trainings: Training[] }[] {
  const groups: { label: string; trainings: Training[] }[] = [];
  for (const t of trainings) {
    const label = new Date(t.date).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.trainings.push(t);
    } else {
      groups.push({ label, trainings: [t] });
    }
  }
  return groups;
}

// ─── Training Card ────────────────────────────────────────────────────────────

function TrainingCard({
  training,
  teamName,
  clubId,
}: {
  training: Training;
  teamName: string;
  clubId: string;
}) {
  const upcoming = isUpcoming(training.date);
  const d = new Date(training.date);

  return (
    <Link
      to={`/club/${clubId}/trainings/${training.id}`}
      className="group flex items-center gap-4 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 hover:border-gray-300 dark:hover:border-gray-700 transition"
    >
      {/* Date column */}
      <div className="shrink-0 w-14 text-center">
        <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 leading-none">
          {d.getDate().toString().padStart(2, "0")}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">
          {d.toLocaleDateString("fr-FR", { month: "short" })}
        </div>
        <div className="text-xs text-gray-400 dark:text-gray-500">{formatTime(training.date)}</div>
      </div>

      {/* Divider */}
      <div className="w-px h-12 bg-gray-100 dark:bg-gray-800 shrink-0" />

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
            {teamName}
          </span>
          {upcoming && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300">
              À venir
            </span>
          )}
        </div>

        <div className="font-semibold text-gray-900 dark:text-gray-100">
          Entraînement
        </div>

        {training.notes && (
          <div className="mt-0.5 text-sm text-gray-500 dark:text-gray-400 truncate">
            {training.notes}
          </div>
        )}
      </div>

      <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition shrink-0" />
    </Link>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function ClubTrainings() {
  const { clubId } = useParams<{ clubId: string }>();

  const [teams, setTeams] = useState<Team[]>([]);
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [teamFilter, setTeamFilter] = useState<string>("all");
  const [viewFilter, setViewFilter] = useState<ViewFilter>("all");

  useEffect(() => {
    if (!clubId) return;
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const teamsRes = await supabase
          .from("teams")
          .select("id, team_name")
          .eq("club_id", clubId)
          .order("team_name", { ascending: true });

        if (teamsRes.error) throw teamsRes.error;

        const teamRows = (teamsRes.data || []) as Team[];
        const teamIds = teamRows.map((t) => t.id);

        let trainingRows: Training[] = [];
        if (teamIds.length > 0) {
          const trainingsRes = await supabase
            .from("trainings")
            .select("id, date, team_id, notes")
            .in("team_id", teamIds)
            .order("date", { ascending: false });

          if (trainingsRes.error) throw trainingsRes.error;
          trainingRows = (trainingsRes.data || []) as Training[];
        }

        if (!cancelled) {
          setTeams(teamRows);
          setTrainings(trainingRows);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || "Impossible de charger les entraînements.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [clubId]);

  const teamMap = useMemo(() => {
    const m = new Map<string, string>();
    teams.forEach((t) => m.set(t.id, t.team_name?.trim() || "Équipe"));
    return m;
  }, [teams]);

  const filtered = useMemo(() => {
    let list = trainings;

    if (teamFilter !== "all") {
      list = list.filter((t) => t.team_id === teamFilter);
    }

    if (viewFilter === "upcoming") {
      list = list.filter((t) => isUpcoming(t.date));
      list = [...list].reverse();
    } else if (viewFilter === "past") {
      list = list.filter((t) => !isUpcoming(t.date));
    }

    return list;
  }, [trainings, teamFilter, viewFilter]);

  const grouped = useMemo(() => groupByMonth(filtered), [filtered]);

  const stats = useMemo(() => ({
    total: trainings.length,
    upcoming: trainings.filter((t) => isUpcoming(t.date)).length,
    past: trainings.filter((t) => !isUpcoming(t.date)).length,
  }), [trainings]);

  const viewTabs: { key: ViewFilter; label: string }[] = [
    { key: "all", label: "Tous" },
    { key: "upcoming", label: "À venir" },
    { key: "past", label: "Passés" },
  ];

  if (loading) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6">
        <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement des entraînements…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 p-4 text-sm text-red-700 dark:text-red-300">
        <div className="inline-flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center sm:text-left">
        <div className="text-sm text-gray-500 dark:text-gray-400">Club</div>
        <div className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center justify-center gap-2 sm:justify-start">
          <Dumbbell className="h-5 w-5 text-gray-500" />
          Entraînements du club
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {stats.total} entraînement{stats.total !== 1 ? "s" : ""} au total — {stats.upcoming} à venir, {stats.past} passés
        </div>
      </div>

      {/* Stat cards */}
      {trainings.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Total", value: stats.total, color: "text-gray-900 dark:text-gray-100" },
            { label: "À venir", value: stats.upcoming, color: "text-indigo-700 dark:text-indigo-300" },
            { label: "Passés", value: stats.past, color: "text-gray-500 dark:text-gray-400" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
              <div className={cx("text-2xl font-bold", s.color)}>{s.value}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-1 gap-1">
          {viewTabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setViewFilter(t.key)}
              className={cx(
                "px-3 py-1.5 rounded-lg text-sm transition",
                viewFilter === t.key
                  ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                  : "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {teams.length > 1 && (
          <div className="inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-1.5">
            <Filter className="h-4 w-4 text-gray-400 shrink-0" />
            <select
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
              className="bg-transparent text-sm text-gray-900 dark:text-gray-100 outline-none cursor-pointer"
            >
              <option value="all">Toutes les équipes</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.team_name?.trim() || "Équipe"}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Training list */}
      {filtered.length === 0 ? (
        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-12 flex flex-col items-center gap-3 text-gray-400 dark:text-gray-500">
          <Dumbbell className="h-8 w-8" />
          <div className="text-sm">
            {trainings.length === 0
              ? "Aucun entraînement enregistré pour ce club."
              : "Aucun entraînement pour ces filtres."}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map((group) => (
            <div key={group.label}>
              <div className="flex items-center gap-3 mb-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 capitalize">
                  {group.label}
                </div>
                <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
                <div className="text-xs text-gray-400 dark:text-gray-500">
                  {group.trainings.length} entraînement{group.trainings.length !== 1 ? "s" : ""}
                </div>
              </div>

              <div className="space-y-2">
                {group.trainings.map((training) => (
                  <TrainingCard
                    key={training.id}
                    training={training}
                    teamName={teamMap.get(training.team_id ?? "") ?? "Équipe"}
                    clubId={clubId!}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
